import { useMemo } from 'react';
import CompletenessMeter from '../../components/CompletenessMeter';
import { useCaseCompleteness } from '../../../shared/hooks/useCaseCompleteness';
import { summarizeClinicalFields } from '../../../shared/utils/clinicalFieldSummary';
import {
    SLIDE_STATUS,
    countByStatus,
    statusLabel,
} from '../../slides/slideStatus';
import styles from '../../CaseBuilderEnhanced.module.css';

const STATUS_RING_COLORS = {
    [SLIDE_STATUS.incomplete]: '#cf222e',
    [SLIDE_STATUS.complete]: '#ea580c',
    [SLIDE_STATUS.released]: '#1a7f37',
};

const STATUS_ORDER = [SLIDE_STATUS.incomplete, SLIDE_STATUS.complete, SLIDE_STATUS.released];

function OverviewCard({ title, onOpen, children, openLabel }) {
    return (
        <div className={styles.overviewCard}>
            <div className={styles.overviewCardHeader}>
                <h3 className={styles.overviewCardTitle}>{title}</h3>
                {onOpen && (
                    <button type="button" className={styles.linkButton} onClick={onOpen}>
                        {openLabel || `Open ${title} →`}
                    </button>
                )}
            </div>
            {children}
        </div>
    );
}

export default function OverviewStep({ doc, workspace, schemas, onGoToStep }) {
    const completeness = useCaseCompleteness(doc);
    const clinical = useMemo(
        () => summarizeClinicalFields(doc, schemas),
        [doc, schemas],
    );
    const slideCounts = useMemo(
        () => countByStatus(doc?.items || []),
        [doc?.items],
    );
    const slideSegments = useMemo(
        () => STATUS_ORDER.map((status) => ({
            value: slideCounts[status] || 0,
            color: STATUS_RING_COLORS[status],
        })),
        [slideCounts],
    );

    const blockCount = Array.isArray(doc?.blocks) ? doc.blocks.length : 0;
    const protocolName = workspace.selectedProtocol?.name
        || (completeness.hasProtocol ? 'Protocol selected' : null);

    const clinicalSublabel = !clinical.hasSchema
        ? 'Choose a schema on Clinical to track fields'
        : `${clinical.filled} of ${clinical.total} fields filled`
            + (clinical.schemaName ? ` · ${clinical.schemaName}` : '');

    const blocksLabel = blockCount
        ? `${blockCount} block${blockCount === 1 ? '' : 's'}`
        : 'No blocks yet';
    const blocksSublabel = protocolName
        || 'No protocol — blocks can be defined manually';

    const slideStatusLine = STATUS_ORDER
        .map((status) => `${slideCounts[status] || 0} ${statusLabel(status).toLowerCase()}`)
        .join(' · ');

    return (
        <div className={styles.stepContent}>
            <div className={styles.overviewGrid}>
                <OverviewCard title="Clinical" onOpen={() => onGoToStep('clinical')}>
                    <CompletenessMeter
                        percent={clinical.percent}
                        label={clinical.hasSchema ? `${clinical.percent}% complete` : 'No schema'}
                        sublabel={clinicalSublabel}
                    />
                </OverviewCard>

                <OverviewCard title="Blocks" onOpen={() => onGoToStep('blocks')}>
                    <CompletenessMeter
                        percent={blockCount > 0 ? 100 : 0}
                        centerLabel={String(blockCount)}
                        label={blocksLabel}
                        sublabel={blocksSublabel}
                        ariaLabel={`${blockCount} blocks`}
                    />
                </OverviewCard>

                <OverviewCard title="Slides" onOpen={() => onGoToStep('slides')}>
                    <CompletenessMeter
                        segments={slideSegments}
                        centerLabel={String(completeness.slideCount)}
                        label={`${completeness.slideCount} slide${completeness.slideCount === 1 ? '' : 's'}`}
                        sublabel={completeness.slideCount ? slideStatusLine : 'No slides yet'}
                        ariaLabel={
                            completeness.slideCount
                                ? `Slides: ${slideStatusLine}`
                                : 'No slides yet'
                        }
                    />
                    <div className={styles.overviewStatusLegend} aria-hidden="true">
                        {STATUS_ORDER.map((status) => (
                            <span key={status} className={styles.overviewStatusItem}>
                                <span
                                    className={styles.overviewStatusDot}
                                    style={{ background: STATUS_RING_COLORS[status] }}
                                />
                                {statusLabel(status)}
                                <strong>{slideCounts[status] || 0}</strong>
                            </span>
                        ))}
                    </div>
                    {doc?.quarantined && (
                        <p className={styles.overviewHint}>
                            Case quarantine is on — released slides stay off the network until you
                            clear it in the header.
                        </p>
                    )}
                    {completeness.releaseReady > 0 && (
                        <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => onGoToStep('slides')}
                        >
                            Release {completeness.releaseReady} ready slide
                            {completeness.releaseReady === 1 ? '' : 's'} →
                        </button>
                    )}
                </OverviewCard>
            </div>
        </div>
    );
}
